/**
 * Traduction et assainissement des messages d'erreur d'authentification.
 * Empêche l'affichage de messages en anglais ou de codes techniques (SQL, paramètres $1, JSON, tokens).
 * Garantit une UX propre, élégante et 100% en français.
 */

export function formatAuthErrorMessage(error: unknown, fallbackMessage: string = "Une erreur est survenue."): string {
  if (!error) return "";

  const rawMsg = typeof error === 'string' ? error : (error as any)?.message || String(error);
  const msg = rawMsg.trim();
  const lower = msg.toLowerCase();

  // 1. Annulations volontaires par l'utilisateur (fermeture de la pop-up Google) -> aucune erreur affichée
  // Couvre les codes GIS ("popup_closed"), les messages du SDK
  // ("Popup closed by the user during getToken call.") et les codes Firebase ("auth/popup-closed-by-user").
  if (
    lower.includes('popup_closed_by_user') ||
    lower.includes('popup-closed-by-user') ||
    lower.includes('popup_closed') ||
    lower.includes('popup closed') ||
    lower.includes('popup-closed') ||
    lower.includes('closed by the user') ||
    lower.includes('user_cancel') ||
    lower.includes('user-cancel') ||
    lower.includes('cancelled-popup') ||
    lower.includes('canceled-popup') ||
    lower.includes('cancelled by user') ||
    lower.includes('canceled by user') ||
    lower.includes('user closed the popup') ||
    lower.includes('user_cancelled') ||
    lower.includes('utilisateur a fermé') ||
    lower.includes('fermeture de la fenetre')
  ) {
    return "";
  }

  // 2. Fenêtre bloquée par un bloqueur de pop-ups
  if (
    lower.includes('popup_failed_to_open') ||
    lower.includes('popup blocked') ||
    lower.includes('fenêtre google a été bloquée') ||
    lower.includes('autoriser les pop-up')
  ) {
    return "La fenêtre Google a été bloquée. Veuillez autoriser les fenêtres pop-up pour continuer.";
  }

  // 3. Problèmes de réseau / scripts bloqués par AdBlock / Brave Shields
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('load failed') ||
    lower.includes('econnrefused') ||
    lower.includes('timeout') ||
    lower.includes('services google') ||
    lower.includes('connexion internet')
  ) {
    return "Impossible de charger les services Google. Vérifiez votre connexion internet ou désactivez votre bloqueur de publicités (AdBlock, Brave Shields) pour ce site.";
  }

  // 4. Refus d'accès ou consentement annulé
  if (
    lower.includes('access_denied') ||
    lower.includes('permission_denied') ||
    lower.includes('denied your request') ||
    lower.includes('user denied') ||
    lower.includes('access denied by')
  ) {
    return "L'autorisation d'accès Google a été refusée. Veuillez réessayer.";
  }

  // 5. Erreurs de configuration Google OAuth
  if (
    lower.includes('idpiframe_initialization_failed') ||
    lower.includes('origin_mismatch') ||
    lower.includes('redirect_uri_mismatch') ||
    lower.includes('unauthorized_client') ||
    lower.includes('invalid_client')
  ) {
    return "La connexion Google est temporairement indisponible. Veuillez vous connecter avec votre e-mail et mot de passe.";
  }

  // 6. Messages techniques internes / SQL / Postgres / JSON (à masquer absolument à l'utilisateur)
  if (
    lower.includes('parameter $') ||
    lower.includes('data type of parameter') ||
    lower.includes('syntax error') ||
    lower.includes('relation "') ||
    lower.includes('violates') ||
    lower.includes('database') ||
    lower.includes('postgres') ||
    lower.includes('jwt') ||
    lower.includes('unexpected token') ||
    lower.includes('internal server error') ||
    lower.includes('bad gateway') ||
    lower.includes('erreur 500') ||
    lower.includes('erreur 502') ||
    lower.includes('erreur 503') ||
    lower.includes('erreur 504')
  ) {
    return "Une erreur technique temporaire est survenue. Veuillez réessayer dans quelques instants.";
  }

  // 7. Messages trop techniques liés aux jetons
  if (
    lower.includes('jeton google requis') ||
    lower.includes('jeton google') ||
    lower.includes('jeton d\'identité') ||
    lower.includes('flux google non supporté')
  ) {
    return "Échec de l'authentification Google. Veuillez réessayer.";
  }

  // 8. Traductions de messages anglais d'authentification
  if (
    lower.includes('invalid credentials') ||
    lower.includes('wrong password') ||
    lower.includes('user not found') ||
    lower.includes('identifiants invalides')
  ) {
    return "Adresse e-mail ou mot de passe incorrect.";
  }

  if (
    lower.includes('already exists') ||
    lower.includes('email already in use') ||
    lower.includes('déjà utilisé') ||
    lower.includes('deja utilise')
  ) {
    return "Cette adresse e-mail est déjà associée à un compte.";
  }

  if (
    lower.includes('password must') ||
    lower.includes('password too short') ||
    lower.includes('au moins 8 caractères')
  ) {
    return "Le mot de passe doit comporter au moins 8 caractères.";
  }

  if (lower.includes('invalid email') || lower.includes('email format')) {
    return "Veuillez renseigner une adresse e-mail valide.";
  }

  if (lower.includes('unauthorized') || lower.includes('non autorisé')) {
    return "Accès non autorisé. Veuillez vous reconnecter.";
  }

  if (lower.includes('session expired') || lower.includes('token expired')) {
    return "Votre session a expiré. Veuillez vous reconnecter.";
  }

  // Nettoyage de préfixes techniques genre "Erreur 400 :" ou "Erreur 401 :"
  if (/^Erreur \d{3}\s*:\s*/i.test(msg)) {
    const cleaned = msg.replace(/^Erreur \d{3}\s*:\s*/i, '').trim();
    if (cleaned && !cleaned.toLowerCase().includes('erreur') && !cleaned.toLowerCase().includes('error')) {
      return cleaned;
    }
    return fallbackMessage;
  }

  return msg || fallbackMessage;
}

/**
 * Assainit tout message d'erreur destiné à l'interface utilisateur générale (notifications, toasts).
 * Masque tout jargon technique de développeur ("token manquant", "JWT", syntaxe SQL, etc.)
 * et formule un message bienveillant et orienté action pour le commerçant.
 */
export function formatUserFacingError(error: unknown, fallbackMessage: string = "Une opération n'a pas pu aboutir."): string {
  if (!error) return "";
  const rawMsg = typeof error === 'string' ? error : (error as any)?.message || (error as any)?.data?.error || String(error);
  const msg = rawMsg.trim();
  const lower = msg.toLowerCase();

  // 1. Session / token expiré ou manquant
  if (
    (error as any)?.status === 401 ||
    lower.includes('token manquant') ||
    lower.includes('non authentifi') ||
    lower.includes('session invalide') ||
    lower.includes('session expir') ||
    lower.includes('reconnexion requise') ||
    lower.includes('jwt') ||
    lower.includes('unauthorized') ||
    lower.includes('access_token')
  ) {
    return "Votre session a expiré. Veuillez vous reconnecter.";
  }

  // 2. Erreurs d'accès / permissions
  if ((error as any)?.status === 403 || lower.includes('interdit') || lower.includes('forbidden')) {
    return "Action non autorisée sur cette ressource.";
  }

  // 3. Quotas
  if ((error as any)?.status === 402 || lower.includes('quota')) {
    return msg || "Quota de réponses atteint pour ce mois.";
  }

  // 4. Erreurs réseau / serveur
  if (
    lower.includes('failed to fetch') ||
    lower.includes('network') ||
    lower.includes('timeout') ||
    lower.includes('econnrefused')
  ) {
    return "Connexion réseau instable. Veuillez vérifier votre accès internet.";
  }

  // 5. Erreurs techniques internes à masquer
  if (
    lower.includes('parameter $') ||
    lower.includes('syntax error') ||
    lower.includes('postgres') ||
    lower.includes('database') ||
    lower.includes('internal server error') ||
    lower.includes('erreur 500') ||
    lower.includes('undefined') ||
    lower.includes('null')
  ) {
    return "Un incident technique temporaire est survenu. Veuillez réessayer.";
  }

  return msg || fallbackMessage;
}
